import { useEffect } from "react";
import { Box, Grid } from "@mui/material";
import { UiAction, UiPage, useRegisterUserActionMutation } from "~/operations";
import { Org } from "~/lib/types";
import {
  NewReleases,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { AssetScoresOverTimeLegacy } from "./asset-scores-over-time-legacy";
import { TopVulnerabilitiesFoundLegacy } from "./top-vulnerabilities-found-legacy";
import { NewThisWeekLegacy } from "./new-this-week-legacy";
import { AssetsTodayLegacy } from "./assets-today-legacy";
import { TopVulnerabilitiesLegacy } from "./top-vulnerabilities-legacy";
import { SpacesGlanceLegacy } from "./spaces-glance-legacy";
import { EndOfLifeLegacy } from "./end-of-life-legacy";
import { DashboardCard } from "./dashboard-card";
import { HomeDashboardHeader } from "~/pages/space/home-dashboard/components/HomeDashboardHeader";
import { setDocumentTitle } from "~/lib/commonUtils";
import { ExplorerIcon, SpacesIcon, VulnerabilitiesIcon } from "~/icons";
import { ScopeType } from "~/hooks/useScope";

interface LegacyOrganizationDashboardPageProps {
  org: Org;
}

export function LegacyOrganizationDashboardPage({
  org,
}: LegacyOrganizationDashboardPageProps) {
  const [registerUserAction] = useRegisterUserActionMutation();

  // Send user action mutation on mount
  useEffect(() => {
    registerUserAction({
      variables: {
        input: {
          action: UiAction.View,
          page: UiPage.OrgDashboard,
          scopeMrn: org.mrn,
        },
      },
    }).then();
  }, []);

  setDocumentTitle(org.name);

  return (
    <Box>
      {/* Heading */}
      <HomeDashboardHeader
        context={ScopeType.Organization}
        title={org.name}
        description={org.description}
      />
      <Grid container spacing={3}>
        <Grid
          container
          spacing={3}
          size={{
            lg: 8,
            md: 6,
            sm: 12,
          }}
        >
          <Grid size={12}>
            <DashboardCard
              title="Asset Scores Over Time"
              icon={<ExplorerIcon />}
              sx={{ height: 336 }}
            >
              <AssetScoresOverTimeLegacy organizationMrn={org.mrn} />
            </DashboardCard>
          </Grid>
          <Grid size={12}>
            <DashboardCard
              title="Top New Vulnerabilities Found"
              subtitle="Critical & High CVSS Vulnerabilities Over The Last 4 Weeks"
              icon={<VulnerabilitiesIcon />}
              sx={{ height: 244 }}
            >
              <TopVulnerabilitiesFoundLegacy organizationMrn={org.mrn} />
            </DashboardCard>
          </Grid>
          <Grid size={12}>
            <DashboardCard
              title="New This Week"
              subtitle="Critical & High CVSS Vulnerabilities From This Week"
              icon={<NewReleases />}
              sx={{ height: 456 }}
            >
              <NewThisWeekLegacy organizationMrn={org.mrn} />
            </DashboardCard>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={3}
          size={{
            lg: 4,
            md: 6,
            sm: 12,
          }}
        >
          <Grid size={12}>
            <DashboardCard
              title="Assets Today"
              icon={<ExplorerIcon />}
              sx={{ height: 336 }}
            >
              <AssetsTodayLegacy organizationMrn={org.mrn} />
            </DashboardCard>
          </Grid>
          <Grid size={12}>
            <DashboardCard
              title="Top Vulnerabilities"
              icon={<VulnerabilitiesIcon />}
              sx={{ height: 256 }}
            >
              <TopVulnerabilitiesLegacy organizationMrn={org.mrn} />
            </DashboardCard>
          </Grid>
          <Grid size={12}>
            <DashboardCard
              title="Spaces At A Glance"
              icon={<SpacesIcon />}
              sx={{ height: 248 }}
            >
              <SpacesGlanceLegacy organizationMrn={org.mrn} />
            </DashboardCard>
          </Grid>
          <Grid size={12}>
            <DashboardCard
              title="End Of Life"
              subtitle="# Of Assets That Are End-Of-Life"
              icon={<WarningAmberIcon />}
              sx={{
                height: 144,
                ".card-header": { mb: 1 },
                ".card-body": { overflow: "hidden" },
              }}
            >
              <EndOfLifeLegacy organizationMrn={org.mrn} />
            </DashboardCard>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
